import React from "react";
import PromotionStatusBadge from "./PromotionStatusBadge";
import { Commend3Model } from "../Types";
import { resolvePromotionBadge } from "../Commend3Helper";
import {
  AUTOSAVE_IDLE_LABEL,
  AUTOSAVE_SAVED_LABEL,
  MODEL_CHIP_LABEL,
} from "../Commend3Constants";

type ModelConfigTopBarProps = {
  model: Commend3Model;
  editable: boolean;
  saved: boolean;
  onBack: () => void;
  onRename: () => void;
};

const svgBase = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const BackIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-[18px] tw-w-[18px]" style={{ ...svgBase, strokeWidth: 1.8 }} aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-3.5 tw-w-3.5" style={{ ...svgBase, strokeWidth: 1.6 }} aria-hidden="true">
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-[15px] tw-w-[15px]" style={{ ...svgBase, strokeWidth: 1.5 }} aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-[13px] tw-w-[13px] tw-shrink-0" style={{ ...svgBase, strokeWidth: 2.2 }} aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-[13px] tw-w-[13px] tw-shrink-0" style={{ ...svgBase, strokeWidth: 1.6 }} aria-hidden="true">
    <path d="M12 2v4" />
    <path d="M12 18v4" />
    <path d="M4.9 4.9l2.9 2.9" />
    <path d="M16.2 16.2l2.9 2.9" />
    <path d="M2 12h4" />
    <path d="M18 12h4" />
    <path d="M4.9 19.1l2.9-2.9" />
    <path d="M16.2 7.8l2.9-2.9" />
  </svg>
);

const ModelConfigTopBar = ({
  model,
  editable,
  saved,
  onBack,
  onRename,
}: ModelConfigTopBarProps) => {
  const badge = resolvePromotionBadge(model);

  return (
    <div
      id="commend3-config-topbar"
      className="tw-flex tw-h-[54px] tw-shrink-0 tw-items-center tw-gap-2.5 tw-bg-white tw-px-[22px]"
      style={{ borderBottom: "0.5px solid #E3E5EC" }}
    >
      <button
        id="commend3-config-back"
        type="button"
        className="tw-flex tw-h-[30px] tw-w-[30px] tw-items-center tw-justify-center tw-rounded-md tw-text-[#59596E]"
        onClick={onBack}
        aria-label="Back to models"
      >
        <BackIcon />
      </button>

      <span
        className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-semibold"
        style={{ background: "#EAF0FF", color: "#030562", letterSpacing: ".02em" }}
      >
        <CubeIcon />
        {MODEL_CHIP_LABEL}
      </span>

      <span className="tw-shrink-0" style={{ width: "1px", height: "18px", background: "#E3E5EC" }} />

      <span
        className="tw-truncate tw-text-[17px] tw-font-semibold tw-text-[#0B0C23]"
        style={{ letterSpacing: "-0.02em" }}
        title={model.name}
      >
        {model.name}
      </span>

      {editable ? (
        <button
          id="commend3-config-rename"
          type="button"
          className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-[#59596E]"
          title="Rename model"
          onClick={onRename}
        >
          <PencilIcon />
        </button>
      ) : null}

      {badge.state !== "notPromoted" ? (
        <PromotionStatusBadge id="commend3-config-status" badge={badge} />
      ) : null}

      <div className="tw-flex-1" />

      <div
        id="commend3-config-autosave"
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-[11px] tw-py-[5px] tw-text-[12px] tw-font-medium"
        style={{
          background: saved ? "#DCFCE7" : "#F4F5F9",
          color: saved ? "#109648" : "#59596E",
          border: `0.5px solid ${saved ? "#86EFAC" : "#E3E5EC"}`,
          transition: "background 150ms ease,color 150ms ease",
        }}
      >
        {saved ? <CheckIcon /> : <SparkIcon />}
        {saved ? AUTOSAVE_SAVED_LABEL : AUTOSAVE_IDLE_LABEL}
      </div>
    </div>
  );
};

export default ModelConfigTopBar;
